import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import apiService from '../services/apiService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const VoiceAIPage = () => {
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [testText, setTestText] = useState('Bonjour, ceci est un test de la voix IA française.');
  const [previewPlaying, setPreviewPlaying] = useState(null);
  const [previewAudio, setPreviewAudio] = useState(null);

  useEffect(() => {
    loadVoices();
  }, []);

  // Cleanup preview audio on unmount
  useEffect(() => {
    return () => {
      if (previewAudio) {
        previewAudio.pause();
        previewAudio.currentTime = 0;
      }
    };
  }, [previewAudio]);

  const loadVoices = async () => {
    try {
      const response = await apiService.voices.getAll();
      setVoices(response.data);
    } catch (error) {
      console.error('Error loading voices:', error);
      toast.error('Erreur lors du chargement des voix');
      setVoices([]);
    }
  };

  const playPreview = async (voice) => {
    // Stop any currently playing preview
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
    }

    try {
      setPreviewPlaying(voice.id);
      toast.info('🔊 Génération de l\'audio en cours...');
      
      // Generate TTS using the same endpoint as the "Tester" button
      const response = await apiService.tts.generate({
        text: testText,
        voice: voice.id,
        format: 'wav',
        use_case: 'voice_test' // Mark for 1-minute cleanup
      });
      
      if (response.data.success) {
        // Create audio element to play TTS
        const audio = new Audio();
        // Construct full URL if audio_url is relative
        const audioUrl = response.data.audio_url.startsWith('http') 
          ? response.data.audio_url 
          : `${BACKEND_URL}${response.data.audio_url}`;
        audio.src = audioUrl;
        setPreviewAudio(audio);
        
        audio.onloadeddata = () => {
          toast.success('🎵 Audio généré avec succès !');
          audio.play();
        };
        
        audio.onerror = () => {
          toast.error('Erreur lors de la lecture audio');
          setPreviewPlaying(null);
          setPreviewAudio(null);
        };
        
        audio.onended = () => {
          setPreviewPlaying(null);
          setPreviewAudio(null);
        };
      } else {
        toast.error(response.data.message || 'Erreur lors de la génération TTS');
        setPreviewPlaying(null);
        setPreviewAudio(null);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Erreur lors de la génération de l\'aperçu');
      setPreviewPlaying(null);
      setPreviewAudio(null);
    }
  };

  const stopPreview = () => {
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
      setPreviewAudio(null);
    }
    setPreviewPlaying(null);
  };

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Voix IA</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Voice Testing Panel */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6">Aperçu des Voix</h2>
            
            {/* Test Text Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Texte de test
              </label>
              <textarea
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Entrez le texte à synthétiser..."
              />
            </div>

            {/* Voice Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {voices.map((voice) => (
                <div 
                  key={voice.id} 
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedVoice?.id === voice.id 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedVoice(voice)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900">{voice.name}</h3>
                      {voice.provider && (
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          voice.provider === 'kokoro' 
                            ? 'bg-purple-100 text-purple-700' 
                            : voice.provider === 'gtts'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {voice.provider.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">{voice.gender}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{voice.language}</p>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (previewPlaying === voice.id) {
                        stopPreview();
                      } else {
                        playPreview(voice);
                      }
                    }}
                    disabled={previewPlaying && previewPlaying !== voice.id}
                    className={`w-full py-2 px-4 rounded-lg transition-colors ${
                      previewPlaying === voice.id
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
                    }`}
                  >
                    {previewPlaying === voice.id ? '⏸️ Arrêter' : '▶️ Aperçu'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Voice Details */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold mb-4">Détails de la Voix</h3>
            
            {selectedVoice ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Nom</label>
                  <p className="text-gray-900">{selectedVoice.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Langue</label>
                  <p className="text-gray-900">{selectedVoice.language}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Genre</label>
                  <p className="text-gray-900">{selectedVoice.gender}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Fréquence</label>
                  <p className="text-gray-900">{selectedVoice.sample_rate} Hz</p>
                </div>
                {selectedVoice.provider && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Fournisseur</label>
                    <p className="text-gray-900">{selectedVoice.provider.toUpperCase()}</p>
                  </div>
                )}
                {selectedVoice.description && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Description</label>
                    <p className="text-gray-900">{selectedVoice.description}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">Sélectionnez une voix pour voir les détails</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceAIPage;